import { useState, useCallback, memo } from 'react';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/components/ui/dialog';
import { ModalLayout, ModalTitle } from '@/components/ui/modal-layout';
import { CreateOrderForm } from './CreateOrderForm';
import { CreateCustomerForm } from './CreateCustomerForm';
import { CreateSourceForm } from './CreateSourceForm';
import { CreateBillingAddressForm } from './CreateBillingAddressForm';
import { CreateOrderProvider, useCreateOrderContext } from './CreateOrderContext';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from '@hooks/useTranslation';
import type { Salesperson, Order } from '@api/orders';
import type { ApiContact, ContactBillingAddress } from '@api/contacts';
import type { OrderSource } from '@api/orderSources';

interface CreateOrderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (order: Order) => void;
  salespersons: Salesperson[];
  currentUser?: { id: number; role: string };
}

/* -------------------------------------------------------------------------- */
/*  Shared building blocks                                                     */
/* -------------------------------------------------------------------------- */

const chevronIcon = (
  <svg width="10" height="18" viewBox="0 0 10 18" fill="none" xmlns="http://www.w3.org/2000/svg" className="shrink-0 text-off-black">
    <path d="M9.9 8.5 1.8 16.8c-.4.4-1.1.4-1.5 0-.4-.4-.4-1 0-1.4L6.9 8.5.3 1.7c-.4-.4-.4-1 0-1.4.4-.4 1.1-.4 1.5 0L9.9 8.5Z" fill="currentColor" />
  </svg>
);

function DialogViewPanel({ children }: { children: React.ReactNode }) {
  return (
    <motion.div
      className="flex min-h-0 flex-1 flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.15 }}
    >
      {children}
    </motion.div>
  );
}

const BreadcrumbTitle = memo(function BreadcrumbTitle({ current, onBack }: { current: string; onBack: () => void }) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-4">
      <button
        type="button"
        onClick={onBack}
        aria-label={`Back to ${t('create_order.breadcrumb')}`}
        className="font-inter text-lg font-semibold text-grey leading-none hover:text-off-black transition-colors"
      >
        {t('create_order.breadcrumb')}
      </button>
      {chevronIcon}
      <span className="font-inter text-lg font-semibold text-off-black leading-none">
        {current}
      </span>
    </div>
  );
});

/* -------------------------------------------------------------------------- */
/*  View variants                                                              */
/* -------------------------------------------------------------------------- */

function CreateOrderView({ salespersons, defaultSalespersonId, onSuccess, onClose }: {
  salespersons: Salesperson[];
  defaultSalespersonId?: number;
  onSuccess: (order: Order) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <DialogViewPanel>
      <ModalLayout
        title={<ModalTitle>{t('create_order.title')}</ModalTitle>}
        onClose={onClose}
        contentClassName="flex min-h-0 flex-col overflow-hidden p-0"
      >
        <CreateOrderForm
          salespersons={salespersons}
          defaultSalespersonId={defaultSalespersonId}
          onSuccess={onSuccess}
        />
      </ModalLayout>
    </DialogViewPanel>
  );
}

function CreateCustomerView({ onClose }: { onClose: () => void }) {
  const { actions } = useCreateOrderContext();
  const { t } = useTranslation();
  const handleBack = useCallback(() => actions.setView('create-order'), [actions]);
  const handleSuccess = useCallback((contact: ApiContact) => {
    actions.setSelectedContact(contact);
    actions.setView('create-order');
  }, [actions]);

  return (
    <DialogViewPanel>
      <ModalLayout
        title={<BreadcrumbTitle current={t('create_customer.title')} onBack={handleBack} />}
        onClose={onClose}
        contentClassName="p-0"
      >
        <CreateCustomerForm onSuccess={handleSuccess} />
      </ModalLayout>
    </DialogViewPanel>
  );
}

function CreateSourceView({ onClose, onPendingChange, isPending }: {
  onClose: () => void;
  onPendingChange: (isPending: boolean) => void;
  isPending: boolean;
}) {
  const { actions } = useCreateOrderContext();
  const { t } = useTranslation();
  const handleBack = useCallback(() => {
    if (isPending) return;
    actions.setView('create-order');
  }, [actions, isPending]);
  const handleSuccess = useCallback((source: OrderSource) => {
    actions.setSelectedSourceId(String(source.id));
    actions.setView('create-order');
  }, [actions]);

  return (
    <DialogViewPanel>
      <ModalLayout
        title={<BreadcrumbTitle current={t('create_source.title')} onBack={handleBack} />}
        onClose={onClose}
        contentClassName="p-0"
      >
        <CreateSourceForm onSuccess={handleSuccess} onPendingChange={onPendingChange} />
      </ModalLayout>
    </DialogViewPanel>
  );
}

function CreateBillingAddressView({ onClose, onPendingChange, isPending }: {
  onClose: () => void;
  onPendingChange: (isPending: boolean) => void;
  isPending: boolean;
}) {
  const { state, actions } = useCreateOrderContext();
  const { t } = useTranslation();
  const contactId = state.selectedContact?.id ?? null;
  const handleBack = useCallback(() => {
    if (isPending) return;
    actions.setView('create-order');
  }, [actions, isPending]);
  const handleSuccess = useCallback((contact: ApiContact, billingAddress: ContactBillingAddress) => {
    actions.applyCreatedBillingAddress(contact, billingAddress);
    actions.setView('create-order');
  }, [actions]);

  if (!contactId) {
    return null;
  }

  return (
    <DialogViewPanel>
      <ModalLayout
        title={<BreadcrumbTitle current={t('create_billing_address.title')} onBack={handleBack} />}
        onClose={onClose}
        contentClassName="p-0"
      >
        <CreateBillingAddressForm
          contactId={contactId}
          onSuccess={handleSuccess}
          onPendingChange={onPendingChange}
        />
      </ModalLayout>
    </DialogViewPanel>
  );
}

/* -------------------------------------------------------------------------- */
/*  Dialog shell                                                               */
/* -------------------------------------------------------------------------- */

function CreateOrderDialogContent({ salespersons, currentUser, onOpenChange, onSuccess }: {
  salespersons: Salesperson[];
  currentUser?: { id: number; role: string };
  onOpenChange: (open: boolean) => void;
  onSuccess?: (order: Order) => void;
}) {
  const { state, actions } = useCreateOrderContext();
  const [isViewSubmitPending, setIsViewSubmitPending] = useState(false);
  const { t } = useTranslation();
  const dialogTitle =
    state.view === 'create-customer'
      ? t('create_customer.title')
      : state.view === 'create-source'
        ? t('create_source.title')
        : state.view === 'create-billing-address'
          ? t('create_billing_address.title')
          : t('create_order.title');

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && isViewSubmitPending) return;

      if (!nextOpen) {
        actions.reset();
        setIsViewSubmitPending(false);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange, actions, isViewSubmitPending]
  );

  const handleClose = useCallback(() => handleOpenChange(false), [handleOpenChange]);

  const handleOrderSuccess = useCallback((order: Order) => {
    handleClose();
    onSuccess?.(order);
  }, [handleClose, onSuccess]);

  return (
    <DialogContent
      aria-describedby={undefined}
      onEscapeKeyDown={(e) => {
        if (isViewSubmitPending) {
          e.preventDefault();
          return;
        }
        handleClose();
      }}
      onInteractOutside={(e) => {
        const target = e.target as HTMLElement;
        if (target.closest('[data-radix-popper-content-wrapper]')) {
          e.preventDefault();
          return;
        }

        if (isViewSubmitPending) {
          e.preventDefault();
        }
      }}
      className="max-w-dialog max-h-[90vh] rounded-input border-dinnerplate-white bg-body p-0 outline-none shadow-none [&>button:last-child]:hidden"
    >
      <VisuallyHidden.Root>
        <DialogTitle>{dialogTitle}</DialogTitle>
      </VisuallyHidden.Root>
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <AnimatePresence mode="wait">
          {state.view === 'create-order' && (
            <motion.div key="create-order" className="flex min-h-0 flex-1 flex-col">
              <CreateOrderView
                salespersons={salespersons}
                defaultSalespersonId={currentUser?.id}
                onSuccess={handleOrderSuccess}
                onClose={handleClose}
              />
            </motion.div>
          )}
          {state.view === 'create-customer' && (
            <motion.div key="create-customer" className="flex min-h-0 flex-1 flex-col">
              <CreateCustomerView onClose={handleClose} />
            </motion.div>
          )}
          {state.view === 'create-source' && (
            <motion.div key="create-source" className="flex min-h-0 flex-1 flex-col">
              <CreateSourceView
                onClose={handleClose}
                onPendingChange={setIsViewSubmitPending}
                isPending={isViewSubmitPending}
              />
            </motion.div>
          )}
          {state.view === 'create-billing-address' && (
            <motion.div key="create-billing-address" className="flex min-h-0 flex-1 flex-col">
              <CreateBillingAddressView
                onClose={handleClose}
                onPendingChange={setIsViewSubmitPending}
                isPending={isViewSubmitPending}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </DialogContent>
  );
}

export function CreateOrderDialog({
  open,
  onOpenChange,
  onSuccess,
  salespersons,
  currentUser,
}: CreateOrderDialogProps) {
  return (
    <CreateOrderProvider>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <CreateOrderDialogContent
          salespersons={salespersons}
          currentUser={currentUser}
          onOpenChange={onOpenChange}
          onSuccess={onSuccess}
        />
      </Dialog>
    </CreateOrderProvider>
  );
}
